import {
  Alert,
  Box,
  Button,
  EditIcon,
  Heading,
  SparklesIcon,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import React from 'react';
import ComponentContainer from '../components/generics/ComponentContainer';
import ComponentHeader from '../components/generics/ComponentHeader';
import ComponentSection from '../components/generics/ComponentSection';
import ComponentSectionItem from '../components/generics/ComponentSectionItem';
import { getStaticMediaUrl } from 'utils/Urls';
import noComponents from '../../../assets/noComponents.svg';
import { FlexibleAssignmentsView } from './FBPAllocationsAndDeclarations/AssignmentContentView';
import { useAssignedComponents } from '../hooks/useAssignedComponents';
import { useIsDeclarationWindowOpen } from '../hooks/useIsDeclarationWindowOpen';

export const FlexibleBenefitsList = ({
  openAllocateFlexibleBenefitsModal,
  userId,
  isAdmin,
  userName,
}: {
  openAllocateFlexibleBenefitsModal: () => void;
  userId: string | number | undefined;
  isAdmin: boolean;
  userName?: string | null;
}) => {
  const {
    assignedFlexibleComponents,
    isFetching,
    totalAssigned,
    totalAllocated,
    totalUnallocated,
    flexibleComponentsData,
    isError: hasError,
    refetchData,
  } = useAssignedComponents({ userId });

  const { isDeclarationWindowOpen } = useIsDeclarationWindowOpen({ userId: userId });

  if (!isFetching && hasError) {
    return (
      <Alert
        description="We are facing some error to fetch the assignment data, please retry"
        isDismissible={false}
        color="negative"
        actions={{
          primary: {
            text: 'Retry',
            onClick: refetchData,
          },
        }}
      />
    );
  }

  return (
    <>
      <ComponentContainer>
        <ComponentSection>
          <ComponentHeader
            icon={<SparklesIcon size="medium" color="surface.icon.primary.normal" />}
            iconBackground={'surface.background.primary.subtle'}
            title={'Flexi components'}
            description={'A non-taxable earning component which is adjusted from Special Allowance'}
            button={
              assignedFlexibleComponents?.length === 0 ||
              hasError ||
              isFetching ? null : isAdmin ? (
                <Button
                  icon={EditIcon}
                  variant="secondary"
                  onClick={openAllocateFlexibleBenefitsModal}>
                  Edit
                </Button>
              ) : !isDeclarationWindowOpen ? null : (
                <Button variant="tertiary" onClick={openAllocateFlexibleBenefitsModal}>
                  Declare
                </Button>
              )
            }
          />
          <ComponentSectionItem>
            <Box borderRadius={'large'} overflow={'hidden'}>
              {isFetching ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="spacing.5"
                  height="100%">
                  <Spinner accessibilityLabel="loading perquisites" />
                  <Text>Loading...</Text>
                </Box>
              ) : !assignedFlexibleComponents || assignedFlexibleComponents?.length === 0 ? (
                <ComponentSectionItem>
                  <Box
                    display="flex"
                    flexDirection={'row'}
                    gap="spacing.8"
                    height="100%"
                    justifyContent={'center'}
                    alignItems={'center'}>
                    <img src={getStaticMediaUrl(noComponents)} alt="no-recurring-deductions" />
                    <Box display={'flex'} flexDirection={'column'} gap={'spacing.3'}>
                      <Heading weight="semibold">No FBP allocation done yet</Heading>
                      <Text size="medium" weight="regular" color="surface.text.gray.muted">
                        {isAdmin
                          ? `Allocate and declare FBP for ${userName || ''}`
                          : 'No flexible components assigned'}
                      </Text>
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        gap={'spacing.5'}
                        alignItems={'center'}
                        marginTop={'spacing.5'}>
                        {isAdmin ? (
                          <Button
                            onClick={() => {
                              openAllocateFlexibleBenefitsModal();
                            }}>
                            + Allocate FBP
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </ComponentSectionItem>
              ) : (
                <FlexibleAssignmentsView
                  assignments={flexibleComponentsData}
                  totalAllocated={totalAllocated ?? 0}
                  totalDeclared={totalAssigned ?? 0}
                  totalUnallocated={totalUnallocated ?? 0}
                />
              )}
            </Box>
          </ComponentSectionItem>
        </ComponentSection>
      </ComponentContainer>
    </>
  );
};
